import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Acl } from '@foundation/acls/model';
import { AclsRepository } from '@foundation/acls/state';
import { AppConfigService } from '@foundation/app/config';
import { TwTeamIcon, TwUserIcon } from '@foundation/icons';
import { NotificationService } from '@foundation/notification';
import { TeamsModals } from '@foundation/teams/modals';
import { TeamsRepository } from '@foundation/teams/state';
import { TeamPillComponent } from '@foundation/teams/ui/team-pill';
import { TranslatePipe, TranslationService } from '@foundation/translations/services';
import { UsersRepository } from '@foundation/users/state';
import { UserPillComponent } from '@foundation/users/ui/user-pill';
import { catchError, map, of } from 'rxjs';
export interface AccessEntry {
	id: string; // Combined ID or subject ID
	who: 'user' | 'team' | 'anonymous';
	whoId?: string;
	displayName: string;
	operations: Record<string, boolean>; // e.g., { read: true, write: false }
	acls: Acl[];
}

@Component({
	selector: 'lib-access-table',
	standalone: true,
	imports: [CommonModule, FormsModule, TwTeamIcon, TwUserIcon, TranslatePipe, TeamPillComponent, UserPillComponent],
	templateUrl: './access-table.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessTableComponent {
	resourceId = input.required<string>();
	resourceKind = input.required<string>();
	hideAnonymous = input(false);

	public usersRepository = inject(UsersRepository);
	public teamsRepository = inject(TeamsRepository);
	private _appConfigService = inject(AppConfigService);
	private _teamsModals = inject(TeamsModals);
	private _aclsRepository = inject(AclsRepository);
	private _notificationService = inject(NotificationService);
	private _translationService = inject(TranslationService);

	// operations from config
	availableOperations = computed(() => this._appConfigService.config$_.acls?.operations || ['read', 'write', 'delete']);

	// items
	acls = signal<Acl[]>([]);
	canWrite = signal(false);

	// Grouped entries to display (Users with ACLs + Teams)
	accessEntries = computed(() => {
		const rawAcls = (this.acls() || []).filter((acl): acl is Acl => !!acl && (!this.hideAnonymous() || acl.who !== 'anonymous'));
		const groups = new Map<string, AccessEntry>();

		// 1. Process current ACLs
		for (const acl of rawAcls) {
			const whoIdStr = acl.whoId || 'none';
			const key = `${acl.who}-${whoIdStr}`;
			if (!groups.has(key)) {
				groups.set(key, {
					id: key,
					who: acl.who as any,
					whoId: acl.whoId ?? undefined,
					displayName: acl.whoId ? (acl.who === 'user' ? 'User' : 'Team') : acl.who,
					operations: {},
					acls: [],
				});
			}
			const entry = groups.get(key)!;
			entry.operations[acl.operation] = true;
			entry.acls.push(acl);
		}

		for (const entry of groups.values()) {
			if (entry.who === 'team' && entry.whoId) {
				const teamDetails = this.teamsRepository.store.objects$$$.value.find((t: any) => t?.id === entry.whoId);
				if (teamDetails) {
					entry.displayName = teamDetails.name || entry.displayName;
				}
			}
		}

		return Array.from(groups.values()).sort((a, b) => {
			if (a.who !== b.who) return a.who === 'user' ? -1 : 1;
			return a.displayName.localeCompare(b.displayName);
		});
	});

	constructor() {
		// ensure teams are loaded (paged)
		this.teamsRepository.store.getObjects$(1, 100, [], undefined, true).subscribe();

		effect(() => {
			const rId = this.resourceId();
			const rKind = this.resourceKind();
			if (rId && rKind) {
				this._fetchAcls();
			}
		});

		// Side effect: trigger fetching details for users already in table
		effect(() => {
			const entries = this.accessEntries();
			const userIds = entries.filter((e) => e.who === 'user' && e.whoId).map((e) => e.whoId!);
			if (userIds.length > 0) {
				this.usersRepository.fetchUsersDetails([...new Set(userIds)]);
			}
		});
	}

	private _fetchAcls() {
		const resourceId = this.resourceId();
		const resourceKind = this.resourceKind();
		if (!resourceId || !resourceKind) return;

		this._aclsRepository
			.getAclsForResource$(resourceId, resourceKind)
			.pipe(
				map((response) => {
					const result = response.result as any;
					const aclsRaw = result?.acls || [];
					const canWriteVal = result?.canWrite || false;

					this.canWrite.set(canWriteVal);
					this.acls.set(
						aclsRaw.map((acl: any, index: number) => ({
							...acl,
							id: acl.id || `acl-${index}-${acl.who}-${acl.whoId || 'none'}-${acl.operation}`,
						}))
					);
				}),
				catchError((err) => {
					console.error('Error fetching ACLs', err);
					return of(null);
				})
			)
			.subscribe();
	}

	private _i18n_updatePermissionsFailed = this._translationService.prep('Failed to update permissions: ');
	private _i18n_shareWithUserTitle = this._translationService.prep('Share with user');
	private _i18n_shareWithUserPrompt = this._translationService.prep('Enter the email address:');
	private _i18n_userNotFound = this._translationService.prep('User not found');
	private _i18n_userNotFoundMessage = this._translationService.prep('The user with email "§email" does not have an account. Do you want to send an invitation?');
	private _i18n_invitationSent = this._translationService.prep('Invitation sent!');
	private _i18n_invitationFailed = this._translationService.prep('Failed to send invitation: ');
	private _i18n_errorFindingUser = this._translationService.prep('Error finding user: ');
	private _i18n_readAccessFailed = this._translationService.prep('Failed to grant read access: ');
	private _i18n_shareWithTeamFailed = this._translationService.prep('Failed to share with team: ');

	togglePermission(entry: AccessEntry, operation: string, event: Event) {
		if (!this.canWrite()) return;

		const checkbox = event.target as HTMLInputElement;
		const action = checkbox.checked ? 'add' : 'remove';

		const rId = this.resourceId();
		const rKind = this.resourceKind();

		if (!entry.whoId && entry.who !== 'anonymous') {
			console.warn('Cannot toggle permission without whoId for', entry);
			return;
		}

		this._aclsRepository
			.updateAclRights$(rId, rKind, entry.who as any, entry.whoId || null, {
				[operation]: action,
			})
			.subscribe({
				next: () => {
					this._fetchAcls();
				},
				error: (err) => {
					const msg = this._i18n_updatePermissionsFailed() + (err.message || 'Unknown error');
					this._notificationService.error(msg);
					checkbox.checked = !checkbox.checked; // Revert checkbox state
				},
			});
	}

	public goToTeam(entry: AccessEntry) {
		if (entry.who !== 'team' || !entry.whoId) return;

		this.teamsRepository.goToTeam(entry.whoId);
	}

	public startSharingFlow() {
		const title = this._i18n_shareWithUserTitle();
		const prompt = this._i18n_shareWithUserPrompt();
		this._notificationService.prompt(title, prompt).closed.subscribe((result) => {
			if (!result?.value) return;
			const email = result.value.trim().toLowerCase();
			this.shareWithEmail(email);
		});
	}

	public shareWithEmail(email: string) {
		this.usersRepository.findUserByEmail$(email).subscribe((response) => {
			if (response.error) {
				const errorCode = response.error.code;

				if (errorCode === 'user_not_found') {
					const title = this._i18n_userNotFound();
					const message = this._i18n_userNotFoundMessage().replace('§email', email);
					this._notificationService.confirm(title, message).closed.subscribe((confirmed) => {
						if (confirmed) {
							this._aclsRepository.inviteByEmail$(email).subscribe({
								next: () => this._notificationService.notify(this._i18n_invitationSent()),
								error: (err) => {
									const msg = this._i18n_invitationFailed() + (err.message || 'Unknown error');
									this._notificationService.error(msg);
								},
							});
						}
					});
				} else {
					const msg = this._i18n_errorFindingUser() + (response.error.title || 'Unknown error');
					this._notificationService.error(msg);
				}
				return;
			}

			if (response.result?.id) {
				const user = response.result;
				this.usersRepository.setUserDetails(user.id, {
					avatarUrl: null,
					publicName: user.pseudo || user.email,
				});

				this._aclsRepository
					.updateAclRights$(this.resourceId(), this.resourceKind(), 'user', response.result.id, {
						read: 'add',
					})
					.subscribe({
						next: () => this._fetchAcls(),
						error: (err) => {
							const msg = this._i18n_readAccessFailed() + (err.message || 'Unknown error');
							this._notificationService.error(msg);
						},
					});
			}
		});
	}

	public selectTeamToShare() {
		this._teamsModals
			.openTeamsSelectionDialog({
				selectionConstraints: { single: true, minTeams: 1, maxTeams: 1 },
			})
			.closed.subscribe((result) => {
				if (result?.teams?.length) {
					const team = result.teams[0];
					this._aclsRepository
						.updateAclRights$(this.resourceId(), this.resourceKind(), 'team', team.id, {
							read: 'add',
						})
						.subscribe({
							next: () => this._fetchAcls(),
							error: (err) => {
								const msg = this._i18n_shareWithTeamFailed() + (err.message || 'Unknown error');
								this._notificationService.error(msg);
							},
						});
				}
			});
	}
}
